// Ant Design Form rule kümeleri
// Backend `app/backend/app/utils/validators.py` ile birebir aynı kuralları uygular,
// böylece frontend ve backend tek kaynaktan doğrulanır.

const RESERVED_USERNAMES = new Set([
  'admin', 'administrator', 'root', 'system', 'superuser',
  'api', 'auth', 'login', 'logout', 'register', 'signup',
  'test', 'demo', 'guest', 'anonymous', 'null', 'undefined',
]);

const SEQUENTIAL_PATTERNS = [
  '123', '234', '345', '456', '567', '678', '789',
  'abc', 'bcd', 'cde', 'def', 'efg', 'fgh', 'ghi',
];

export const usernameRules = ({ required = true } = {}) => [
  ...(required ? [{ required: true, message: 'Kullanıcı adı gerekli' }] : []),
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      const v = value.trim();
      if (v.length < 3) return Promise.reject(new Error('Kullanıcı adı en az 3 karakter olmalı'));
      if (v.length > 50) return Promise.reject(new Error('Kullanıcı adı en fazla 50 karakter olabilir'));
      if (!/^[a-zA-Z0-9_-]+$/.test(v)) return Promise.reject(new Error('Sadece harf, rakam, alt çizgi ve tire kullanılabilir'));
      if (!/^[a-zA-Z0-9]/.test(v)) return Promise.reject(new Error('Harf veya rakam ile başlamalı'));
      if (v.endsWith('_') || v.endsWith('-')) return Promise.reject(new Error('Alt çizgi veya tire ile bitemez'));
      if (/[_-]{2,}/.test(v)) return Promise.reject(new Error('Ardışık özel karakter kullanılamaz'));
      if (RESERVED_USERNAMES.has(v.toLowerCase())) return Promise.reject(new Error(`"${v}" rezerve kelimedir`));
      return Promise.resolve();
    },
  },
];

export const emailRules = ({ required = true } = {}) => [
  ...(required ? [{ required: true, message: 'E-posta gerekli' }] : []),
  { type: 'email', message: 'Geçerli bir e-posta adresi girin' },
  { max: 255, message: 'E-posta en fazla 255 karakter olabilir' },
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      if (/\.\./.test(value)) return Promise.reject(new Error('E-posta ardışık nokta içeremez'));
      return Promise.resolve();
    },
  },
];

export const fullNameRules = ({ required = true } = {}) => [
  ...(required ? [{ required: true, message: 'Ad soyad gerekli' }] : []),
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      const v = value.trim();
      if (v.length < 1 || v.length > 100) return Promise.reject(new Error('Ad soyad 1-100 karakter olmalı'));
      if (!/^[\p{L}\s\-'.]+$/u.test(v)) return Promise.reject(new Error('Sadece harf, boşluk, tire, kesme işareti ve nokta kullanılabilir'));
      return Promise.resolve();
    },
  },
];

export const phoneRules = ({ required = false } = {}) => [
  ...(required ? [{ required: true, message: 'Telefon gerekli' }] : []),
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      const v = value.trim();
      if (v.length > 25) return Promise.reject(new Error('Telefon en fazla 25 karakter olabilir'));
      if (!/^[\d+\s\-().]+$/.test(v)) return Promise.reject(new Error('Sadece rakam, +, boşluk, tire, parantez ve nokta kullanılabilir'));
      const digits = v.replace(/\D/g, '');
      if (digits.length < 7 || digits.length > 15) return Promise.reject(new Error('Telefon 7-15 rakam içermelidir'));
      return Promise.resolve();
    },
  },
];

export const taxNumberRules = ({ required = false } = {}) => [
  ...(required ? [{ required: true, message: 'Vergi numarası gerekli' }] : []),
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      const v = value.trim();
      if (v.length < 8 || v.length > 20) return Promise.reject(new Error('Vergi numarası 8-20 karakter olmalı'));
      if (!/^[a-zA-Z0-9-]+$/.test(v)) return Promise.reject(new Error('Sadece harf, rakam ve tire kullanılabilir'));
      if (!/\d/.test(v)) return Promise.reject(new Error('En az bir rakam içermeli'));
      if (v.startsWith('-') || v.endsWith('-')) return Promise.reject(new Error('Tire ile başlayamaz veya bitemez'));
      return Promise.resolve();
    },
  },
];

export const customerNameRules = ({ required = true } = {}) => [
  ...(required ? [{ required: true, message: 'Müşteri adı gerekli' }] : []),
  { max: 200, message: 'Müşteri adı en fazla 200 karakter olabilir' },
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      const v = value.trim();
      if (v.length < 1) return Promise.reject(new Error('Müşteri adı boş olamaz'));
      if (!/^[a-zA-ZçÇğĞıİöÖşŞüÜ0-9\s\-'.&(),]+$/.test(v)) {
        return Promise.reject(new Error('Sadece harf, rakam, boşluk ve -.&\',() karakterleri'));
      }
      return Promise.resolve();
    },
  },
];

export const contactPersonRules = ({ required = true } = {}) => [
  ...(required ? [{ required: true, message: 'İletişim kişisi gerekli' }] : []),
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      const v = value.trim();
      if (v.length < 1 || v.length > 100) return Promise.reject(new Error('İletişim kişisi 1-100 karakter olmalı'));
      if (!/^[a-zA-ZçÇğĞıİöÖşŞüÜ\s\-'.]+$/.test(v)) {
        return Promise.reject(new Error('Sadece harf, boşluk, tire, kesme işareti ve nokta'));
      }
      return Promise.resolve();
    },
  },
];

export const addressRules = ({ required = true } = {}) => [
  ...(required ? [{ required: true, message: 'Adres gerekli' }] : []),
  { max: 500, message: 'Adres en fazla 500 karakter olabilir' },
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      if (/[<>"`\\]/.test(value)) return Promise.reject(new Error('Adres < > " ` \\ karakterlerini içeremez'));
      return Promise.resolve();
    },
  },
];

export const passwordRules = (policy, { required = true, forEdit = false } = {}) => {
  const minLength = policy?.min_length || 8;
  const requireUppercase = policy?.require_uppercase !== false;
  const requireNumbers = policy?.require_numbers !== false;
  const requireSpecial = policy?.require_special_chars !== false;

  const rules = [];
  if (required && !forEdit) {
    rules.push({ required: true, message: 'Şifre gerekli' });
  }
  rules.push({
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      if (value.length < minLength) return Promise.reject(new Error(`Şifre en az ${minLength} karakter olmalı`));
      if (!/[a-z]/.test(value)) return Promise.reject(new Error('En az bir küçük harf gerekli'));
      if (requireUppercase && !/[A-Z]/.test(value)) return Promise.reject(new Error('En az bir büyük harf gerekli'));
      if (requireNumbers && !/\d/.test(value)) return Promise.reject(new Error('En az bir rakam gerekli'));
      if (requireSpecial && !/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(value)) {
        return Promise.reject(new Error('En az bir özel karakter gerekli'));
      }
      const lower = value.toLowerCase();
      for (const p of SEQUENTIAL_PATTERNS) {
        if (lower.includes(p)) return Promise.reject(new Error('Ardışık karakter dizisi içeremez (123, abc)'));
      }
      return Promise.resolve();
    },
  });
  return rules;
};

export const passwordHelpText = (policy, forEdit = false) => {
  if (forEdit) return 'Şifre değiştirmek istemiyorsanız boş bırakın';
  if (!policy) return 'Şifre kuralları yükleniyor...';
  return (policy.rules || []).join('. ') + '.';
};
